import { useCallback, useEffect, useMemo, useState } from 'react';
import { CodeNameModel } from 'components/common/types';
import { SortDir, SortSettings } from 'components/common/types/SortDir.types';
import {
  FilterLabels,
  filterOptions,
  isCategoryEqual,
  sortOptions,
  updateSelectedOptions,
} from 'components/pages/Planner/PlannerSecondaryPanel/utils';
import usePrevious from './usePrevious';

const filterEmpty = (obj: Record<string, CodeNameModel[]>): Record<string, CodeNameModel[]> => {
  return Object.fromEntries(Object.entries(obj).filter(([, value]) => !Array.isArray(value) || value.length > 0));
};

const getCategoryValue = (category: string): string => {
  if (category in FilterLabels) return FilterLabels[category as keyof typeof FilterLabels];
  return category;
};

interface UseCategoryOptions {
  onCategoryDirectionSort: () => void;
  searchText: string;
  openCategories: string[];
  onFilterTextChange: (value: string) => void;
  selectedOptions: CodeNameModel[];
  setSelectedOptions: React.Dispatch<React.SetStateAction<CodeNameModel[]>>;
  filteredOptions: Record<string, CodeNameModel[]>;
  getNumberOfFilteredOptions: (option: string, _selectedOptions: CodeNameModel[]) => number;
  sortDirection: SortDir;
  includedOptions: CodeNameModel[];
  excludedOptions: CodeNameModel[];
  isOptionSelected: (code: string, category: string, mediaOwner?: string) => boolean;
  isOptionIncluded: (code: string, category: string, mediaOwner?: string) => boolean;
  onOptionIncludeExcludeChange: (option: CodeNameModel) => void;
  onToggleCategory: (category: string) => void;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  onCategoryIncludeAll: (category: string) => void;
  onCategoryExcludeAll: (category: string) => void;
  getCategoryIncludeState: (category: string) => boolean | undefined;
}

interface UseCategoryOptionsParams {
  options: Record<string, CodeNameModel[]>;
  initialSelectedOptions: CodeNameModel[];
  isServerSideFiltering?: boolean;
  categoryOrder?: string[];
}

export const useCategoryOptions = ({
  options,
  initialSelectedOptions,
  isServerSideFiltering,
  categoryOrder,
}: UseCategoryOptionsParams): UseCategoryOptions => {
  const [selectedOptions, setSelectedOptions] = useState(initialSelectedOptions);
  const [filteredOptions, setFilteredOptions] = useState<Record<string, CodeNameModel[]>>(options);
  const [sortDirection, setSortDirection] = useState(SortDir.ASC);
  const [searchText, setSearchText] = useState<string>('');
  const [openCategories, setOpenCategories] = useState<string[]>([]);
  const prevSearchText = usePrevious(searchText) || '';

  useEffect(() => {
    setSelectedOptions(initialSelectedOptions);
  }, [initialSelectedOptions]);

  useEffect(() => {
    let optionsCopy = options;

    if (!isServerSideFiltering) optionsCopy = filterOptions(options, searchText);

    const filteredEmptyOptions = filterEmpty(optionsCopy);

    const sortedOptions = sortOptions(filteredEmptyOptions, sortDirection, categoryOrder);
    setFilteredOptions(sortedOptions);

    if (searchText !== prevSearchText) {
      if (searchText.length > 2) {
        const autoOpenedCategories = Object.keys(sortedOptions).filter(
          (category) => sortedOptions[category].length > 0 && sortedOptions[category].length <= 3,
        );
        setOpenCategories(autoOpenedCategories);
      } else {
        setOpenCategories([]);
      }
    }
  }, [options, sortDirection, searchText, prevSearchText, isServerSideFiltering, categoryOrder]);

  const onFilterTextChange = (value: string): void => {
    setSearchText(value);
  };

  const getOptionKey = useCallback((code: string, category: string, mediaOwner?: string): string => {
    const key = `${code}:${getCategoryValue(category)}`;
    return mediaOwner ? `${key}:${mediaOwner}` : key;
  }, []);

  const selectedOptionsMap = useMemo(() => {
    const map = new Map<string, CodeNameModel>();
    selectedOptions.forEach((option) => {
      map.set(getOptionKey(option.code, option.category ?? '', option.mediaOwner), option);
    });
    return map;
  }, [selectedOptions, getOptionKey]);

  const isOptionSelected = useCallback(
    (code: string, category: string, mediaOwner?: string) =>
      selectedOptionsMap.has(getOptionKey(code, category, mediaOwner)),
    [selectedOptionsMap, getOptionKey],
  );

  const onOptionIncludeExcludeChange = useCallback((option: CodeNameModel): void => {
    setSelectedOptions((prev) => updateSelectedOptions(prev, option));
  }, []);

  const isOptionIncluded = useCallback(
    (code: string, category: string, mediaOwner?: string): boolean =>
      selectedOptionsMap.get(getOptionKey(code, category, mediaOwner))?.include || false,
    [selectedOptionsMap, getOptionKey],
  );

  const includedOptions = useMemo(() => selectedOptions.filter((option) => option.include), [selectedOptions]);
  const excludedOptions = useMemo(
    () => selectedOptions.filter((option) => option.include === false),
    [selectedOptions],
  );

  const getNumberOfFilteredOptions = (option: string, _selectedOptions: CodeNameModel[]): number => {
    const categoryOptions = options[option];
    if (!categoryOptions) return 0;

    return _selectedOptions.filter((selectedOption) =>
      categoryOptions.some(
        (formatOption: CodeNameModel) =>
          formatOption.code === selectedOption.code &&
          isCategoryEqual(option, selectedOption.category) &&
          formatOption.mediaOwner === selectedOption.mediaOwner,
      ),
    ).length;
  };

  const onCategoryDirectionSort = useCallback((): void => {
    setSortDirection(SortSettings[sortDirection].nextDir);
  }, [sortDirection]);

  const onToggleCategory = useCallback(
    (category: string) => {
      if (openCategories.includes(category))
        setOpenCategories(openCategories.filter((openCategory) => openCategory !== category));
      else setOpenCategories([...openCategories, category]);
    },
    [openCategories],
  );

  const updateCategoryOptions = useCallback(
    (prev: CodeNameModel[], category: string, categoryItems: CodeNameModel[], include: boolean): CodeNameModel[] => {
      const categoryValue = getCategoryValue(category);

      const updated = prev.map((option) =>
        isCategoryEqual(category, option.category) &&
        categoryItems.some((item) => item.code === option.code && item.mediaOwner === option.mediaOwner)
          ? { ...option, include }
          : option,
      );

      const existingKeys = prev.reduce<Set<string>>((acc, option) => {
        if (isCategoryEqual(category, option.category)) {
          acc.add(getOptionKey(option.code, option.category ?? '', option.mediaOwner));
        }
        return acc;
      }, new Set());

      const newItems = categoryItems.reduce<CodeNameModel[]>((acc, { code, name, mediaOwner }) => {
        if (!existingKeys.has(getOptionKey(code, categoryValue, mediaOwner))) {
          acc.push({ code, name, category: categoryValue, include, ...(mediaOwner ? { mediaOwner } : {}) });
        }
        return acc;
      }, []);

      return [...updated, ...newItems];
    },
    [getOptionKey],
  );

  const setCategoryIncludeState = useCallback(
    (category: string, include: boolean) => {
      const categoryItems = filteredOptions[category];
      if (!categoryItems) return;

      setSelectedOptions((prev) => updateCategoryOptions(prev, category, categoryItems, include));
    },
    [filteredOptions, updateCategoryOptions],
  );

  const onCategoryIncludeAll = useCallback(
    (category: string) => setCategoryIncludeState(category, true),
    [setCategoryIncludeState],
  );

  const onCategoryExcludeAll = useCallback(
    (category: string) => setCategoryIncludeState(category, false),
    [setCategoryIncludeState],
  );

  const getCategoryIncludeState = useCallback(
    (category: string): boolean | undefined => {
      const categoryItems = options[category];
      if (!categoryItems?.length) return undefined;

      const matchingSelected = selectedOptions.filter(
        (option) =>
          isCategoryEqual(category, option.category) &&
          categoryItems.some((item) => item.code === option.code && item.mediaOwner === option.mediaOwner),
      );

      if (matchingSelected.length !== categoryItems.length) return undefined;

      if (matchingSelected.every((option) => option.include)) return true;
      if (matchingSelected.every((option) => option.include === false)) return false;

      return undefined;
    },
    [options, selectedOptions],
  );

  return {
    onCategoryDirectionSort,
    searchText,
    openCategories,
    onFilterTextChange,
    selectedOptions,
    setSelectedOptions,
    filteredOptions,
    getNumberOfFilteredOptions,
    sortDirection,
    includedOptions,
    excludedOptions,
    isOptionSelected,
    isOptionIncluded,
    onOptionIncludeExcludeChange,
    onToggleCategory,
    setSearchText,
    onCategoryIncludeAll,
    onCategoryExcludeAll,
    getCategoryIncludeState,
  };
};
